import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLiveMarket } from "../../lib/useLiveMarket";
import Sparkline from "../Sparkline";

const COIN_CONFIG = [
  { name: "Bitcoin", symbol: "BTC", id: "bitcoin", bg: "#F7931A", letter: "₿" },
  { name: "Ethereum", symbol: "ETH", id: "ethereum", bg: "#627EEA", letter: "Ξ" },
  { name: "Solana", symbol: "SOL", id: "solana", bg: "#9945FF", letter: "◎" },
  { name: "Tether", symbol: "USDT", id: "tether", bg: "#26A17B", letter: "₮" },
];

const COIN_IDS = COIN_CONFIG.map((c) => c.id);

function fmtPrice(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1) {
    return n.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });
  }
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 6,
  });
}

function fmtChange(pct) {
  if (!Number.isFinite(pct)) return "0.00%";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

export default function CryptoUpdates() {
  const live = useLiveMarket(COIN_IDS);

  return (
    <section className="cm-landing-section bg-cm-surface border-y border-[var(--cm-border)]" data-testid="crypto-updates">
      <div className="cm-container">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="cm-landing-section-label !text-emerald-600 !tracking-[0.16em]">
                Live markets
              </span>
            </div>
            <h2 className="cm-landing-section-title">Top crypto updates</h2>
            <p className="mt-2 text-[14px] text-cm-grey">Live prices from Binance via CoinMBX.</p>
          </div>
          <Link
            to="/markets"
            className="inline-flex items-center gap-2 text-[14px] font-bold text-cm-blue shrink-0"
            data-testid="crypto-updates-view-all"
          >
            View all markets
            <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {COIN_CONFIG.map((c, i) => {
            const ticker = live[c.id];
            const hasData = !!ticker;
            const up = hasData ? ticker.up : true;
            const price = hasData ? fmtPrice(ticker.price) : "—";
            const change = hasData ? fmtChange(ticker.changePct) : "—";
            const sparkVals = hasData ? ticker.sparkline : null;

            return (
              <Link
                key={c.symbol}
                to={`/trade/spot/${c.symbol}-USDT`}
                className="group cm-landing-feature-card rounded-[16px] p-5 lg:p-6 block border-t-[3px] border-t-cm-blue"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`coin-card-${c.symbol.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-10 h-10 rounded-full grid place-items-center text-white text-[16px] font-bold"
                      style={{ background: c.bg }}
                    >
                      {c.letter}
                    </span>
                    <div>
                      <div className="text-[14px] font-bold text-cm-navy group-hover:text-cm-blue transition-colors">
                        {c.name}
                      </div>
                      <div className="text-[11px] text-cm-grey">{c.symbol}/USDT</div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-1 rounded-md ${
                      up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                    }`}
                    data-testid={`coin-change-${c.symbol.toLowerCase()}`}
                  >
                    {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    {change}
                  </span>
                </div>
                <div
                  className="text-[24px] font-extrabold tracking-tight text-cm-navy tabular-nums"
                  data-testid={`coin-price-${c.symbol.toLowerCase()}`}
                >
                  {price}
                </div>
                <div className="mt-4 h-10 opacity-90 group-hover:opacity-100 transition-opacity">
                  <Sparkline
                    values={sparkVals}
                    color={up ? "#16A34A" : "#DC2626"}
                    width={200}
                    height={40}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
